<script setup lang="ts">
import type { ThemeColourChoice, ThemeColourOutput } from "../../composables/useThemeColours";
import {
  formatThemeColour,
  parseThemeColour,
  themeColourPreview,
} from "../../composables/useThemeColours";

const props = withDefaults(defineProps<{
  modelValue?: string;
  output?: ThemeColourOutput;
  placeholder?: string;
}>(), {
  modelValue: "",
  output: "css",
  placeholder: "Theme colour or CSS value",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
}>();

const open = shallowRef(false);
const { knownNames, builtInGroups, customChoices } = useThemeColours();
const parsed = computed(() => parseThemeColour(props.modelValue, {
  output: props.output,
  knownNames: knownNames.value,
}));
const preview = computed(() => themeColourPreview(
  props.modelValue,
  props.output,
  knownNames.value,
));

function select(choice: ThemeColourChoice) {
  emit("update:modelValue", formatThemeColour(choice.name, props.output, knownNames.value));
  open.value = false;
}

function updateRaw(value: unknown) {
  emit("update:modelValue", formatThemeColour(value, props.output, knownNames.value));
}
</script>

<template>
  <div class="flex min-w-0 items-center gap-1.5">
    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button
          type="button"
          variant="outline"
          size="icon"
          class="size-8 shrink-0 rounded-sm bg-background"
          title="Choose a theme colour"
          aria-label="Choose a theme colour"
        >
          <span
            class="size-4 rounded-sm border border-foreground/15"
            :style="{ background: preview }"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" class="w-[340px] p-2">
        <div class="max-h-[300px] space-y-3 overflow-y-auto">
          <section
            v-for="(group, groupIndex) in builtInGroups"
            :key="group.id"
            :class="groupIndex ? 'border-t pt-2.5' : ''"
          >
            <p class="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {{ group.label }}
            </p>
            <div class="grid grid-cols-2 gap-1">
              <button
                v-for="choice in group.choices"
                :key="choice.name"
                type="button"
                class="flex h-8 min-w-0 items-center gap-2 rounded-sm px-1.5 text-left text-[11.5px] hover:bg-accent"
                @click="select(choice)"
              >
                <span
                  class="size-4 shrink-0 rounded-sm border border-foreground/15"
                  :style="{ background: `var(--${choice.name})` }"
                />
                <span class="truncate">{{ choice.label }}</span>
                <Icon
                  v-if="parsed.kind === 'theme' && parsed.name === choice.name"
                  mode="svg"
                  size="12"
                  name="lucide:check"
                  class="ml-auto shrink-0"
                />
              </button>
            </div>
          </section>

          <section v-if="customChoices.length">
            <p class="mb-1.5 border-t px-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Site colours
            </p>
            <div class="grid grid-cols-2 gap-1">
              <button
                v-for="choice in customChoices"
                :key="choice.name"
                type="button"
                class="flex h-8 min-w-0 items-center gap-2 rounded-sm px-1.5 text-left text-[11.5px] hover:bg-accent"
                @click="select(choice)"
              >
                <span
                  class="size-4 shrink-0 rounded-sm border border-foreground/15"
                  :style="{ background: `var(--${choice.name})` }"
                />
                <span class="truncate">{{ choice.label }}</span>
                <Icon
                  v-if="parsed.kind === 'theme' && parsed.name === choice.name"
                  mode="svg"
                  size="12"
                  name="lucide:check"
                  class="ml-auto shrink-0"
                />
              </button>
            </div>
          </section>
        </div>
      </PopoverContent>
    </Popover>

    <Input
      :model-value="modelValue"
      class="h-8 min-w-0 rounded-sm bg-muted font-mono text-[11.5px]"
      :placeholder="placeholder"
      aria-label="Custom CSS colour value"
      title="Enter a theme token, hex value, oklch colour, or other CSS colour"
      @update:model-value="updateRaw"
    />
  </div>
</template>
